'use client';

import type { HandleChangeAnswerType } from '../model';
import type { AnswerChoicesGridType, AnswerUserType } from '~/model/AnswerUserType';
import type { ChoiceType } from '~/model/ChoiceType';
import type { SubQuestionType } from '~/model/SubQuestionType';
import classNames from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import { useFocus } from '~/hooks/useFocus';
import { useHorizontalScroll } from '~/hooks/useHorizontalScroll';

export const GridTypeQuestion = ({
  answerUser,
  answer_choices,
  questionId,
  subQuestions,
  handleChangeAnswer,
  type,
  langValue,
  disabled = false,
}: GridTypeQuestionPropsType) => {
  const [inputRef, setInputFocus] = useFocus();

  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const { isOverflowing, hasScrolled, hasReachedEnd } = useHorizontalScroll(
    scrollableContainerRef,
  );

  const gridClassNames = classNames({
    'control-grid--overflowing': isOverflowing,
    'control-grid--scrolled': hasScrolled,
    'control-grid--end': hasReachedEnd,
  });

  useLayoutEffect(() => {
    setInputFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current]);

  return (
    <div data-testid="grid" className={`control-grid ${gridClassNames}`}>
      <div className="control-grid__scrollable" ref={scrollableContainerRef}>
        <table className="control-grid__table" aria-labelledby="questionTitle">
          <thead>
            {answer_choices && (
              <tr>
                <th />
                {answer_choices.map((choice: ChoiceType) => (
                  <th
                    lang={langValue}
                    key={choice.id}
                    scope="column"
                    id={`choice-title-${choice.id}`}
                    data-testid="choice-title"
                  >
                    {choice.title}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {subQuestions
              && subQuestions.length > 0
              && subQuestions.map(
                (subQuestion: SubQuestionType, indexSQ: number) => {
                  const subQuestionUri = subQuestion['@id'];
                  const subQuestionId = subQuestion.id;
                  const subQuestionTitle = subQuestion.title;
                  return (
                    <tr key={subQuestionUri} data-testid="subquestion">
                      <th
                        lang={langValue}
                        scope="row"
                        id={`subquestion-title-${subQuestion.id}`}
                      >
                        {subQuestionTitle}
                      </th>
                      {answer_choices
                        && answer_choices.length > 0
                        && answer_choices.map((choice, index) => {
                          const choiceUri = choice['@id'];

                          const userChoices: AnswerChoicesGridType
                            = answerUser[questionId]
                              && (answerUser[questionId]
                                .answer_choices as AnswerChoicesGridType);

                          const isChecked
                            = !!userChoices?.[subQuestionUri]?.length
                              && userChoices?.[subQuestionUri]?.every(
                                uri => uri === choiceUri,
                              );

                          const shouldHaveRef = indexSQ === 0 && index === 0;

                          return (
                            <td key={`${subQuestionUri}-${choice.id}`}>
                              <div>
                                <input
                                  ref={shouldHaveRef ? inputRef : null}
                                  id={`choice-${subQuestionId}-${choice.id}`}
                                  className="vtmn-radio-button"
                                  data-testid="radio"
                                  data-cy="radio"
                                  type="radio"
                                  name={`question-grid-${subQuestionUri}`}
                                  value={choiceUri}
                                  checked={isChecked}
                                  onChange={e => handleChangeAnswer(
                                    e.target.value,
                                    questionId,
                                    type,
                                    subQuestionUri,
                                  )}
                                  disabled={disabled}
                                  aria-labelledby={`subquestion-title-${subQuestion.id} choice-title-${choice.id}`}
                                />
                                <label
                                  data-testid="label"
                                  htmlFor={`choice-${subQuestionId}-${choice.id}`}
                                  aria-label={`${subQuestionTitle} : ${choice.title}`}
                                >
                                  <span lang={langValue}>{choice.title}</span>
                                </label>
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                  );
                },
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export interface GridTypeQuestionPropsType {
  answerUser: AnswerUserType;
  answer_choices: ChoiceType[];
  questionId: string;
  subQuestions: SubQuestionType[];
  handleChangeAnswer: HandleChangeAnswerType;
  type: string;
  langValue: string;
  disabled?: boolean;
}
